import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, Building2, Calendar, User, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact/submissions`);
      if (response.data.success) {
        setSubmissions(response.data.submissions);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch submissions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-black mb-2">Contact Submissions</h1>
            <p className="text-gray-400">View and manage all contact form submissions</p>
          </div>
          <Button
            onClick={fetchSubmissions}
            className="bg-[#d9fb06] text-black hover:bg-[#d9fb06]/90 rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[#1a1a1c] border-white/10">
            <CardContent className="p-6">
              <div className="text-4xl font-black text-[#d9fb06] mb-2">{submissions.length}</div>
              <div className="text-gray-400">Total Submissions</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1c] border-white/10">
            <CardContent className="p-6">
              <div className="text-4xl font-black text-[#d9fb06] mb-2">
                {submissions.filter(s => s.status === 'new').length}
              </div>
              <div className="text-gray-400">New Messages</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1c] border-white/10">
            <CardContent className="p-6">
              <div className="text-4xl font-black text-[#d9fb06] mb-2">
                {submissions.filter(s => s.business).length}
              </div>
              <div className="text-gray-400">With Business Info</div>
            </CardContent>
          </Card>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d9fb06] mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading submissions...</p>
          </div>
        )}

        {/* Submissions List */}
        {!loading && submissions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No submissions yet</p>
          </div>
        )}

        {!loading && submissions.length > 0 && (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <Card key={submission.id} className="bg-[#1a1a1c] border-white/10 hover:border-[#d9fb06]/50 transition-all">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2 flex items-center gap-2">
                        <User className="w-5 h-5 text-[#d9fb06]" />
                        {submission.name}
                        {submission.status === 'new' && (
                          <span className="ml-2 px-2 py-1 bg-[#d9fb06] text-black text-xs font-bold rounded-full">
                            NEW
                          </span>
                        )}
                      </CardTitle>
                      <div className="space-y-2 text-sm text-gray-400">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${submission.email}`} className="text-[#d9fb06] hover:underline">
                            {submission.email}
                          </a>
                        </div>
                        {submission.business && (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {submission.business}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(submission.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-[#0f0f10] p-4 rounded-lg">
                    <p className="text-gray-300 whitespace-pre-wrap">{submission.message}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
